import { Component, createRef } from 'react';
import ReactPlayer from 'react-player/lib/players/YouTube';
import { connect } from 'react-redux';
import cn from 'classnames';
import debounce from 'lodash.debounce';

import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { DESKTOP_PDP_VIDEO } from 'constants/appConstants';
import { trackEvent } from 'helpers/analytics';
import { evVideoClick, evVideoImpression } from 'events/symphony';
import { track } from 'apis/amethyst';
import { onEvent } from 'helpers/EventHelpers';
import MelodyVideoPlaceholder from 'components/common/melodyVideo/MelodyVideoPlaceholder';

import css from 'styles/components/landing/melodyVideo/melodyVideo.scss';

const DEBOUNCE_THRESHOLD = 250;
const REGEX_TABLET = /tablet/i;
const REGEX_MOBILE = /mobile/i;

export class MelodyVideoPlayer extends Component {
  constructor() {
    super();
    this.containerRef = createRef();
  }

  state = {
    isPlaying: false,
    intervals: [],
    source: ''
  };

  componentDidMount() {
    const { slotDetails, slotName, slotIndex } = this.props;
    const { componentName, autoplay } = slotDetails;

    this.buildResponsiveVideo();

    onEvent(window, 'resize', this.handleResize, null, this);

    if (componentName === DESKTOP_PDP_VIDEO || autoplay) {
      this.setIsPlaying(true);
    }

    track(() => ([evVideoImpression, { slotDetails, slotName, slotIndex }]));
  }

  // Since <source> tags don't work with videos, manually set the video source based on screen breakpoints
  // This won't be responsive in a sense that adjusting screen sizes manually will change the video dynamically,
  // But at least we can load the proper content for screens at the beginning using melody breakpoints
  buildResponsiveVideo = () => {
    const { slotDetails: { mobileVideoSrc, tabletVideoSrc, src } } = this.props;
    const containerRef = this.containerRef.current;
    if (containerRef instanceof HTMLElement) {
      // we're using the `::before` pseudo-element to let us know the screen size
      const screen = window.getComputedStyle(containerRef, '::before').content;
      if (mobileVideoSrc && REGEX_MOBILE.test(screen)) {
        this.setState({ source: mobileVideoSrc });
      } else if (tabletVideoSrc && REGEX_TABLET.test(screen)) {
        this.setState({ source: tabletVideoSrc });
      } else {
        this.setState({ source: src });
      }
    }
  };

  handleResize = debounce(this.buildResponsiveVideo, DEBOUNCE_THRESHOLD);

  onVideoEnded = () => {
    this.sendVideoAnalytics('ENDED');
    this.setState({ ended: true, isPlaying: false, lastPlayedTime: undefined, intervals: [] });
  };

  onVideoPause = () => {
    if (this.state.isPlaying) {
      this.setIsPlaying(false);
      this.sendVideoAnalytics('PAUSED');
      this.setState({ intervals: [] }); // clear intervals array in case user scrolls to previous time
      track(() => ([evVideoClick, { ...this.props, interactionType: 'PAUSE' }]));
    }
  };

  onVideoPlay = () => {
    const { isPlaying } = this.state;
    const el = this.melodyVideoElement;
    const yt = this.youTubeVideoElement;

    if (!isPlaying) {
      this.setIsPlaying(true);
      track(() => ([evVideoClick, { ...this.props, interactionType: 'PLAY' }]));
    }

    if (el) {
      el.controls = true;
      this.trackVideoPlay(el.currentTime, el.duration);
    } else if (yt) {
      this.trackVideoPlay(yt.getCurrentTime(), yt.getDuration());
    }
  };

  handlePlayerClick = e => {
    e.preventDefault(); // Stop browser's native pause/play functionality
    const el = this.melodyVideoElement;
    const { isPlaying } = this.state;

    if (el) {
      el.muted = false; // unmute if autoplayed initially

      if (isPlaying) {
        el.pause();
      } else {
        el.play();
      }
    }
  };

  onVideoTimeUpdate = () => {
    const { isPlaying } = this.state;

    if (isPlaying) {
      const el = this.melodyVideoElement;
      const yt = this.youTubeVideoElement;

      if (el) {
        this.trackCurrentTime(el.currentTime);
      } else if (yt) {
        this.trackCurrentTime(yt.getCurrentTime());
      }
    }
  };

  setIsPlaying = isPlaying => {
    this.setState({ isPlaying });
  };

  makeTextHeading(heading) {
    return heading && <h2>{heading}</h2>;
  }

  sendVideoAnalytics = (action, currentTime) => {
    const { slotDetails: { heading, productId } } = this.props;

    if (productId) {
      trackEvent(`TE_PRODUCTVIDEO_${action}`, `${productId}${currentTime ? `:${currentTime}` : ''}`);
    } else {
      trackEvent(`TE_MELODYVIDEO_${action}`, `${heading}${currentTime ? `:${currentTime}` : ''}`);
    }

    this.setState({ lastEventName: action });
  };

  trackCurrentTime = currentRawTime => {
    const { intervals, lastPlayedTime } = this.state;
    const currentTime = Math.floor(currentRawTime);

    // assume a difference greater than 1 between currentTime and lastPlayedTime happened because of the user seeking
    if (Math.abs(currentTime - lastPlayedTime) > 1) {
      this.sendVideoAnalytics('SOUGHT', `${lastPlayedTime}:${currentTime}`);
      track(() => ([evVideoClick, { ...this.props, interactionType: 'SEEKED' }]));
    }

    // send analytics of video playtime periodically every 5 seconds (but not for the first "play")
    if (currentTime && (currentTime % 5 === 0) && !intervals.includes(currentTime)) {
      this.setState({ intervals: intervals.concat([currentTime]), lastPlayedTime: currentTime });
      this.sendVideoAnalytics('TIMEUPDATED', currentTime);
    } else {
      this.setState({ lastPlayedTime: currentTime });
    }
  };

  trackVideoPlay = (currentTime, duration) => {
    // if current time is 0 and we've ended, then this is a replay
    // depending on browser and clicking on control vs on video currentTime will either be 0 or the end if replaying
    const { ended, lastEventName } = this.state;
    const eventName = ended && (Math.floor(currentTime) === 0 || currentTime === duration) ? 'REPLAYED' : 'PLAYED';

    // YouTube (or react-player) issue where onPlay fires twice on a replay
    if (lastEventName !== 'REPLAYED') {
      this.sendVideoAnalytics(eventName);
    }
  };

  render() {
    const { isPlaying, source } = this.state;
    const {
      slotName,
      slotDetails: { poster, heading, isEmbedded, componentName, autoplay, monetateId, type, loop = false },
      heightValue,
      isYouTubeVideo,
      widthValue,
      showPlaceholder
    } = this.props;

    const isDesktopPdp = componentName === DESKTOP_PDP_VIDEO;
    // autoplay video if desktop PDP or set by SiteMerch
    const isAutoPlay = isDesktopPdp || autoplay;
    const posterImage = poster || '';
    const config = {
      file: {
        attributes: {
          poster: posterImage,
          preload: 'none'
        }
      },
      youtube: {
        playerVars: {
          cc_load_policy: 1,
          cc_lang_pref: 'en'
        }
      }
    };

    const containerClass = cn(css.melodyVideo,
      { [css.landingPageVideo] : componentName === 'melodyVideoPlayer' },
      { [css.fullwidth] : type === 'fullwidth' }
    );

    if (ReactPlayer.canPlay(source) && isYouTubeVideo) {
      return (
        <div
          className={containerClass}
          data-slot-id={slotName}
          data-monetate-id={monetateId}
          ref={this.containerRef}
        >
          {this.makeTextHeading(heading)}
          <MelodyVideoPlaceholder showPlaceholder={showPlaceholder} widthValue={widthValue} heightValue={heightValue}>
            <ReactPlayer
              url={source}
              playing={!!isAutoPlay}
              onPlay={this.onVideoPlay}
              onPause={this.onVideoPause}
              onEnded={this.onVideoEnded}
              onProgress={this.onVideoTimeUpdate}
              playsinline={true}
              controls={true}
              width={widthValue}
              height={heightValue}
              config={config}
              loop={loop}
              ref={el => this.youTubeVideoElement = el} />
          </MelodyVideoPlaceholder>
        </div>
      );
    } else if (!isEmbedded) { // normal melodyVideo component
      return (
        <div className={containerClass} data-slot-id={slotName} ref={this.containerRef}>
          {this.makeTextHeading(heading)}
          <video
            onClick={this.handlePlayerClick}
            ref={el => this.melodyVideoElement = el}
            src={source}
            poster={posterImage}
            autoPlay={!!isAutoPlay}
            preload={isAutoPlay ? 'auto' : 'none'}
            muted={isAutoPlay && !isDesktopPdp} // do not mute pdp autoplay videos
            height={heightValue}
            controls={false}
            playsInline={true}
            onPlay={this.onVideoPlay}
            onPause={this.onVideoPause}
            onEnded={this.onVideoEnded}
            loop={loop}
            onTimeUpdate={this.onVideoTimeUpdate}></video>
          <button type="button" onClick={this.handlePlayerClick} className={cn(css.play, { [css.playHidden || css.playVisible]: isPlaying })}></button>
        </div>
      );
    } else { // otherwise, try and render as an iframe
      return (
        <div className={css.iframeVideo} data-slot-id={slotName} ref={this.containerRef}>
          {this.makeTextHeading(heading)}
          <iframe
            src={source}
            title={`${heading} video}`}
            allowFullScreen></iframe>
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  product: state.product
});

MelodyVideoPlayer.defaultProps = {
  isYouTubeVideo: false,
  heightValue: 'auto',
  showPlaceholder: false
};

const ConnectedMelodyVideoPlayer = connect(mapStateToProps, {
})(MelodyVideoPlayer);

export default withErrorBoundary('ConnectedMelodyVideoPlayer', ConnectedMelodyVideoPlayer);
